import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  ChatMessage,
  BusinessUnit,
  ScreenContext,
  FormContext,
} from "@/types";

export type ScreenContextPreference = "ask" | "always" | "never";
export type FormContextPreference = "ask" | "always" | "never";

export interface PageContext {
  pathname?: string;
  page: string;
  filters?: Record<string, unknown>;
  screenContext?: ScreenContext;
}

interface ChatState {
  contextFormSection: { pathname: string; id: string } | null;
  chatId: string | null;
  messages: ChatMessage[];
  isThinking: boolean;
  isStreaming: boolean;
  chatGeneration: number;
  streamingContent: string | null;
  currentProgress: string | null;
  followUpQuestions: string[] | null;
  selectedBusinessUnit: BusinessUnit | null | undefined;
  selectedBusinessUnitContextKey: string | null;
  logEmissionEnabled: boolean;
  sidebarOpen: boolean;
  pageContext: PageContext | null;
  includeScreenContext: boolean | null;
  pendingScreenDetection: boolean;
  includeFormContext: boolean | null;
  pendingFormDetection: boolean;
  /** User preference for whether to attach on-screen context automatically. */
  screenContextPreference: ScreenContextPreference;
  /** Live context for the form the user is filling (facility-wizard copilot). */
  formContext: FormContext | null;
  /** User preference for whether to attach live form context automatically. */
  formContextPreference: FormContextPreference;

  setChatId: (id: string | null) => void;
  addMessage: (msg: ChatMessage) => void;
  completeStreamingMessage: (msg: ChatMessage) => void;
  updateLastUserMessage: (updater: (msg: ChatMessage) => ChatMessage) => void;
  setMessages: (msgs: ChatMessage[]) => void;
  setStreamingContent: (content: string | null) => void;
  setIsThinking: (thinking: boolean) => void;
  setIsStreaming: (streaming: boolean) => void;
  setCurrentProgress: (progress: string | null) => void;
  setFollowUpQuestions: (questions: string[] | null) => void;
  setSelectedBusinessUnit: (
    bu: BusinessUnit | null | undefined,
    contextKey?: string,
  ) => void;
  setLogEmissionEnabled: (enabled: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setPageContext: (ctx: PageContext | null) => void;
  setIncludeScreenContext: (include: boolean | null) => void;
  setPendingScreenDetection: (pending: boolean) => void;
  setIncludeFormContext: (include: boolean | null) => void;
  setPendingFormDetection: (pending: boolean) => void;
  setScreenContextPreference: (pref: ScreenContextPreference) => void;
  setFormContext: (ctx: FormContext | null) => void;
  setFormContextPreference: (pref: FormContextPreference) => void;
  resetChat: () => void;
}

const initialState = {
  contextFormSection: null as { pathname: string; id: string } | null,
  chatId: null as string | null,
  messages: [] as ChatMessage[],
  isThinking: false,
  isStreaming: false,
  chatGeneration: 0,
  streamingContent: null as string | null,
  currentProgress: null as string | null,
  followUpQuestions: null as string[] | null,
  selectedBusinessUnit: undefined as BusinessUnit | null | undefined,
  selectedBusinessUnitContextKey: null as string | null,
  logEmissionEnabled: false,
  sidebarOpen: false,
  pageContext: null as PageContext | null,
  includeScreenContext: null as boolean | null,
  pendingScreenDetection: false,
  includeFormContext: null as boolean | null,
  pendingFormDetection: false,
  screenContextPreference: "ask" as ScreenContextPreference,
  formContext: null as FormContext | null,
  formContextPreference: "ask" as FormContextPreference,
};

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      ...initialState,

      setChatId: (id) => set({ chatId: id }),

      addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),

      completeStreamingMessage: (msg) =>
        set((s) => ({
          messages: [...s.messages, msg],
          streamingContent: null,
          isThinking: false,
          isStreaming: false,
          currentProgress: null,
        })),

      updateLastUserMessage: (updater) =>
        set((s) => {
          const lastUserIdx = s.messages.findLastIndex(
            (m) => m.role === "user",
          );
          if (lastUserIdx === -1) return s;
          const updated = [...s.messages];
          updated[lastUserIdx] = updater(updated[lastUserIdx]);
          return { messages: updated };
        }),

      setMessages: (msgs) => set({ messages: msgs }),

      setStreamingContent: (content) => set({ streamingContent: content }),

      setIsThinking: (thinking) => set({ isThinking: thinking }),
      setIsStreaming: (streaming) => set({ isStreaming: streaming }),

      setCurrentProgress: (progress) => set({ currentProgress: progress }),

      setFollowUpQuestions: (questions) =>
        set({ followUpQuestions: questions }),

      setSelectedBusinessUnit: (bu, contextKey) =>
        set({
          selectedBusinessUnit: bu,
          selectedBusinessUnitContextKey: contextKey ?? null,
        }),

      setLogEmissionEnabled: (enabled) => set({ logEmissionEnabled: enabled }),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      setPageContext: (ctx) => set({ pageContext: ctx }),

      setIncludeScreenContext: (include) =>
        set({ includeScreenContext: include }),

      setPendingScreenDetection: (pending) =>
        set({ pendingScreenDetection: pending }),

      setIncludeFormContext: (include) => set({ includeFormContext: include }),

      setPendingFormDetection: (pending) =>
        set({ pendingFormDetection: pending }),

      setScreenContextPreference: (pref) =>
        set({ screenContextPreference: pref }),

      setFormContext: (ctx) => set({ formContext: ctx }),

      setFormContextPreference: (pref) => set({ formContextPreference: pref }),

      resetChat: () => {
        set((state) => ({
          chatGeneration: state.chatGeneration + 1,
          contextFormSection: null,
          chatId: null,
          messages: [],
          isThinking: false,
          isStreaming: false,
          streamingContent: null,
          currentProgress: null,
          followUpQuestions: null,
          selectedBusinessUnit: undefined,
          selectedBusinessUnitContextKey: null,
          logEmissionEnabled: false,
          sidebarOpen: false,
          includeScreenContext: null,
          pendingScreenDetection: false,
          includeFormContext: null,
          pendingFormDetection: false,
        }));
        try {
          useChatStore.persist.clearStorage();
        } catch {
          // ignore
        }
      },
    }),
    {
      name: "gm-ai-chat",
      partialize: (s) => ({
        screenContextPreference: s.screenContextPreference,
        formContextPreference: s.formContextPreference,
      }),
    },
  ),
);
